import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, Check, Loader2, XCircle, Mic, Building2, RefreshCw, AlertTriangle, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useOrg } from "@/hooks/useOrg";
import { useCustodianConnections } from "@/hooks/useCustodianConnections";
import { useEntity } from "@/hooks/useEntity";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export default function IntegrationsSettings() {
  const { currentOrg } = useOrg();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { entities, isLoading: isLoadingEntities } = useEntity();
  const [selectedEntityId, setSelectedEntityId] = useState<string>("");

  // Custodian connections (Flanks)
  const {
    connections: custodianConnections,
    isLoading: isLoadingCustodians,
    connect: connectBank,
    disconnect: disconnectBank,
    syncNow,
    reAuthenticate,
    handleCallback,
  } = useCustodianConnections();

  // Handle Flanks callback
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    if (code && state) {
      handleCallback.mutate({ code, state });
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams, handleCallback, setSearchParams]);

  // Fetch email integrations
  const { data: integrations, isLoading } = useQuery({
    queryKey: ["email-integrations", currentOrg?.id],
    queryFn: async () => {
      if (!currentOrg?.id) return [];
      const { data, error } = await supabase
        .from("email_integrations")
        .select("*")
        .eq("org_id", currentOrg.id);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentOrg?.id,
  });

  // Connect Gmail
  const connectGmail = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("gmail-oauth-init");
      if (error) throw error;
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to connect Gmail");
    },
  });

  // Connect Outlook
  const connectOutlook = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("outlook-oauth-init");
      if (error) throw error;
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to connect Outlook");
    },
  });

  // Disconnect integration
  const disconnectIntegration = useMutation({
    mutationFn: async (integrationId: string) => {
      const { error } = await supabase
        .from("email_integrations")
        .delete()
        .eq("id", integrationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-integrations"] });
      toast.success("Integration disconnected");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to disconnect");
    },
  });

  // Fetch Fireflies integration
  const { data: firefliesIntegration, isLoading: isLoadingFireflies } = useQuery({
    queryKey: ["fireflies-integration", currentOrg?.id],
    queryFn: async () => {
      if (!currentOrg?.id) return null;
      const { data, error } = await supabase
        .from("fireflies_integrations")
        .select("*")
        .eq("org_id", currentOrg.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!currentOrg?.id,
  });

  // Test Fireflies connection
  const testFireflies = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("fireflies-sync", {
        body: { action: "test_connection", org_id: currentOrg?.id }
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Connection failed");
      return data;
    },
    onSuccess: () => {
      toast.success("Successfully connected to Fireflies!");
      queryClient.invalidateQueries({ queryKey: ["fireflies-integration"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to connect to Fireflies");
    },
  });

  // Sync Fireflies transcripts
  const syncFireflies = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("fireflies-sync", {
        body: { 
          action: "fetch_transcripts", 
          org_id: currentOrg?.id,
          limit: 20 
        }
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Synced ${data?.count || 0} transcripts from Fireflies`);
      queryClient.invalidateQueries({ queryKey: ["fireflies-integration"] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to sync transcripts");
    },
  });

  // Disconnect Fireflies
  const disconnectFireflies = useMutation({
    mutationFn: async () => {
      if (!currentOrg?.id) throw new Error("No org selected");
      const { error } = await supabase
        .from("fireflies_integrations")
        .delete()
        .eq("org_id", currentOrg.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fireflies-integration"] });
      toast.success("Fireflies integration disconnected");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to disconnect");
    },
  });

  const gmailIntegration = integrations?.find((i) => i.provider === "gmail");
  const outlookIntegration = integrations?.find((i) => i.provider === "outlook");

  return (
    <div className="flex-1 space-y-4 px-6 py-6">
      
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Integrations</h1>
        <p className="text-muted-foreground">
          Connect external services and tools
        </p>
      </div>

      {/* Bank & Custodian Connections */}
      <Card className="mb-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Bank & Custodian Connections
              </CardTitle>
              <CardDescription className="mt-1">
                Connect your custodian accounts to automatically sync portfolio positions
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={selectedEntityId} onValueChange={setSelectedEntityId}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select entity..." />
                </SelectTrigger>
                <SelectContent>
                  {entities.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id}>
                      {entity.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => {
                  if (!selectedEntityId) {
                    toast.error("Please select an entity first");
                    return;
                  }
                  connectBank.mutate(selectedEntityId);
                }}
                disabled={connectBank.isPending || isLoadingCustodians || !selectedEntityId}
              >
                {connectBank.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Connect Bank
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingCustodians ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : custodianConnections && custodianConnections.length > 0 ? (
            <div className="space-y-3">
              {custodianConnections.map((conn) => (
                <div
                  key={conn.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    {conn.bank_logo_url ? (
                      <img
                        src={conn.bank_logo_url}
                        alt={conn.bank_name || "Bank"}
                        className="h-8 w-8 rounded"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{conn.bank_name || "Unknown Bank"}</p>
                      <p className="text-xs text-muted-foreground">
                        {conn.last_sync_at
                          ? `Last sync: ${new Date(conn.last_sync_at).toLocaleString()}`
                          : "Never synced"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {conn.status === "sca_required" ? (
                      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Re-auth Required
                      </Badge>
                    ) : conn.status === "error" ? (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        <XCircle className="h-3 w-3 mr-1" />
                        Error
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        <Check className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                    )}
                    {conn.status === "sca_required" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => reAuthenticate.mutate(conn.id)}
                      >
                        Re-authenticate
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => syncNow.mutate(conn.id)}
                        disabled={syncNow.isPending}
                      >
                        {syncNow.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => disconnectBank.mutate(conn.id)}
                      disabled={disconnectBank.isPending}
                    >
                      <XCircle className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No bank accounts connected</p>
              <p className="text-sm">
                Connect your custodian to automatically sync positions
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Gmail Integration */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Gmail
              </CardTitle>
              {gmailIntegration && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <Check className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              )}
            </div>
            <CardDescription>
              Connect your Gmail account to sync emails
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {gmailIntegration ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Connected as: <span className="font-medium">{gmailIntegration.email_address}</span>
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => disconnectIntegration.mutate(gmailIntegration.id)}
                  disabled={disconnectIntegration.isPending}
                >
                  {disconnectIntegration.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      Disconnect
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => connectGmail.mutate()}
                disabled={connectGmail.isPending || isLoading}
              >
                {connectGmail.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  "Connect Gmail"
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Outlook Integration */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Microsoft Outlook
              </CardTitle>
              {outlookIntegration && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <Check className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              )}
            </div>
            <CardDescription>
              Connect your Outlook account to sync emails
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {outlookIntegration ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Connected as: <span className="font-medium">{outlookIntegration.email_address}</span>
                </p>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => disconnectIntegration.mutate(outlookIntegration.id)}
                  disabled={disconnectIntegration.isPending}
                >
                  {disconnectIntegration.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      Disconnect
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => connectOutlook.mutate()}
                disabled={connectOutlook.isPending || isLoading}
              >
                {connectOutlook.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  "Connect Outlook"
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Fireflies Integration */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Mic className="h-5 w-5" />
                Fireflies.ai
              </CardTitle>
              {firefliesIntegration?.is_active && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <Check className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
              )}
            </div>
            <CardDescription>
              Automatically import meeting transcripts and notes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {firefliesIntegration?.is_active ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Last synced: {firefliesIntegration.last_sync_at 
                    ? new Date(firefliesIntegration.last_sync_at).toLocaleString()
                    : "Never"}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => syncFireflies.mutate()}
                    disabled={syncFireflies.isPending}
                  >
                    {syncFireflies.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      "Sync Now"
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => disconnectFireflies.mutate()}
                    disabled={disconnectFireflies.isPending}
                  >
                    {disconnectFireflies.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Disconnecting...
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4" />
                        Disconnect
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <Button
                onClick={() => testFireflies.mutate()}
                disabled={testFireflies.isPending || isLoadingFireflies}
              >
                {testFireflies.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting...
                  </>
                ) : (
                  "Connect Fireflies"
                )}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* QuickBooks Online */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>QuickBooks Online</CardTitle>
              <Badge variant="outline" className="bg-muted">
                Coming Soon
              </Badge>
            </div>
            <CardDescription>
              Connect your QuickBooks Online account for accounting data sync
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Sync chart of accounts, bills, invoices, payments, and more
            </p>
          </CardContent>
        </Card>

        {/* Xero */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Xero</CardTitle>
              <Badge variant="outline" className="bg-muted">
                Coming Soon
              </Badge>
            </div>
            <CardDescription>
              Connect your Xero account for accounting data sync
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Sync chart of accounts, bills, invoices, payments, and more
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cloud Storage</CardTitle>
            <CardDescription>
              Google Drive, OneDrive
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Coming soon...</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Communication</CardTitle>
            <CardDescription>
              Slack, Microsoft Teams
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">Coming soon...</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
